import { useRef, useState, useEffect } from 'react';
import { Typography } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import InfiniteScroll from 'react-infinite-scroller';

import Button from 'src/components/Button';
import Checkbox from 'src/components/Checkbox';
import { useClickAway } from 'src/hooks/useClickAway';
import { useStyles } from './AppBar.styles';

interface PublisherFilterProps {
  publishers: string[];
  selectedPublishers: string[];
  excludedPublishers: string[];
  isOpen: boolean;
  isSmallSidebar?: boolean;
  handleClose: () => void;
  handleSave: (publishers: string[]) => void;
  handleExclude: (publishers: string[]) => void;
}

export const PublisherFilter = ({
  publishers,
  selectedPublishers,
  excludedPublishers,
  isOpen,
  isSmallSidebar,
  handleClose,
  handleSave,
  handleExclude,
}: PublisherFilterProps) => {
  const classes = useStyles();
  const [searchKey, setSearchKey] = useState<string>('');
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const ref = useRef<any>();
  const itemsPerPage = 100;
  const [hasMore, setHasMore] = useState(true);
  const [records, setrecords] = useState(itemsPerPage);
  const [isExclude, setIsExclude] = useState(false);

  const loadMore = () => {
    const data = publishers.filter(
      (item: any) =>
        !searchKey ||
        (item.publisher &&
          item.publisher.toLowerCase().includes(searchKey.toLowerCase()))
    );
    if (records >= data.length) {
      setHasMore(false);
    } else {
      setTimeout(() => {
        setrecords(records + itemsPerPage);
      }, 500);
    }
  };

  useClickAway({ ref, handleClickAway: handleClose });

  useEffect(() => {
    if (isOpen) {
      setSearchKey('');
      setSelectedOptions(isExclude ? excludedPublishers : selectedPublishers);
      setHasMore(true);
      setrecords(itemsPerPage);
    }
  }, [isOpen, isExclude]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className={classes.selectionContainer}
      style={{ left: isSmallSidebar ? 372 : 552 }}
      ref={ref}
    >
      <div className={classes.selectionMain}>
        <div className={classes.selectionHeader}>
          <Typography
            style={{
              fontSize: 16,
              color: 'gray',
              fontWeight: 'normal',
            }}
          >
            Find podcasts by their publisher
          </Typography>
          <div>
            <div className={classes.buttonGroup}>
              <Button
                title='Include'
                color={isExclude ? 'secondary' : 'primary'}
                variant={isExclude ? 'outlined' : 'contained'}
                fontSize='sm'
                height={30}
                onClick={() => {
                  setIsExclude(false);
                }}
              />
              <Button
                title='Exclude'
                color={isExclude ? 'primary' : 'secondary'}
                variant={isExclude ? 'contained' : 'outlined'}
                fontSize='sm'
                height={30}
                onClick={() => {
                  setIsExclude(true);
                }}
              />
            </div>
          </div>
          <div className={classes.searchContainer}>
            <SearchIcon />
            <input
              placeholder='Search publishers...'
              value={searchKey}
              onChange={(e) => setSearchKey(e.target.value)}
            />
          </div>
          <Typography
            style={{
              fontSize: 12,
              fontWeight: 'normal',
            }}
          >
            {!searchKey
              ? `Showing ${publishers.length} results`
              : `Showing ${
                  publishers.filter(
                    (item: any) =>
                      item.publisher &&
                      item.publisher
                        .toLowerCase()
                        .includes(searchKey.toLowerCase())
                  ).length
                } results for ${searchKey}`}
          </Typography>
        </div>
        <div className={classes.selectionOptions}>
          <InfiniteScroll
            pageStart={0}
            loadMore={loadMore}
            hasMore={hasMore}
            loader={<h4 className='loader'>Loading...</h4>}
            useWindow={false}
          >
            {publishers
              .filter(
                (item: any) =>
                  !searchKey ||
                  (item.publisher &&
                    item.publisher
                      .toLowerCase()
                      .includes(searchKey.toLowerCase()))
              )
              .slice(0, records)
              .map((item: any) => (
                <div key={item.publisher} className={classes.selectOption}>
                  <Checkbox
                    name={item.publisher}
                    label=''
                    checked={selectedOptions.includes(item.publisher)}
                    noPadding={true}
                    onChange={() => {
                      if (selectedOptions.includes(item.publisher)) {
                        const options = selectedOptions.filter(
                          (option) => option !== item.publisher
                        );
                        setSelectedOptions(options);
                      } else {
                        const options = [...selectedOptions, item.publisher];
                        setSelectedOptions(options);
                      }
                    }}
                    disabled={
                      isExclude ? false : excludedPublishers.includes(item)
                    }
                  />
                  <Typography
                    style={{
                      fontSize: 14,
                      fontWeight: 'normal',
                      color:
                        !isExclude && excludedPublishers.includes(item)
                          ? 'rgba(0,0,0,0.45)'
                          : 'rgba(0,0,0,0.87)',
                    }}
                  >
                    {item.publisher} ({item.count || 0})
                  </Typography>
                </div>
              ))}
          </InfiniteScroll>
        </div>
      </div>
      <div className={classes.selctionActions}>
        <Button
          title='Clear Selection'
          color='primary'
          variant='text'
          fontSize='sm'
          height={30}
          onClick={() => {
            setSelectedOptions([]);
          }}
        />
        <Button
          title={isExclude ? 'Exclude Publisher' : 'Select Publisher'}
          color='primary'
          fontSize='sm'
          height={30}
          onClick={() => {
            if (isExclude) {
              handleExclude(selectedOptions);
              handleSave(
                selectedPublishers.filter(
                  (item) => !selectedOptions.includes(item)
                )
              );
            } else {
              handleSave(selectedOptions);
            }
          }}
        />
      </div>
    </div>
  );
};
